<script setup>
import { formatCurrency, formatNumber, formatPercent } from '../Utils/numberFormat.js'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import StatChart from './StatChart.vue'
import { serviceColor } from '../Constants/serviceColors.js'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'

const { httpErrorsHandler } = useHttpErrorsHandler()

const REVENUE_HELP = "Somme des prix des demandes gagnées ce mois-ci, mise à jour chaque nuit. Le prix n'est pas renseigné sur toutes les demandes : ce montant est donc un plancher."
const SERVICES_HELP = "Prestations des demandes créées ce mois-ci. Les demandes sans prestation renseignée sont écartées. Au-delà des 6 plus fréquentes, le reste est regroupé sous « Autres »."

const loading = ref(true)
const dashboard = ref({ year: null, month: null, centers: [], services: [] })

const formatAmount = (value) => formatCurrency(value, 'EUR', { maximumFractionDigits: 0 })

const monthLabel = computed(() => {
    if (!dashboard.value.year) return ''
    const date = new Date(dashboard.value.year, dashboard.value.month - 1, 1)
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date)
    return label.charAt(0).toUpperCase() + label.slice(1)
})

const today = new Date()
const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
const daysElapsed = today.getDate()
const daysLeft = daysInMonth - daysElapsed

// Part du mois écoulée : c'est l'avancement « attendu » à date pour tenir l'objectif.
const expectedProgress = Math.round(daysElapsed / daysInMonth * 1000) / 10

const COLORS = { success: '#0ab39c', secondary: '#3577f1', warning: '#f7b84b' }

/**
 * Situation d'un centre par rapport au rythme nécessaire pour tenir l'objectif du mois.
 */
const pace = (center) => {
    if (center.progress >= 100) {
        return { key: 'success', label: 'Objectif atteint', icon: 'ri-trophy-line' }
    }
    if (center.progress >= expectedProgress) {
        return { key: 'success', label: 'En avance sur le rythme', icon: 'ri-arrow-right-up-line' }
    }
    if (center.progress >= expectedProgress * 0.8) {
        return { key: 'secondary', label: 'Proche du rythme', icon: 'ri-arrow-right-line' }
    }
    return { key: 'warning', label: 'En retard sur le rythme', icon: 'ri-arrow-right-down-line' }
}

const projection = (center) => Math.round(center.revenue / daysElapsed * daysInMonth)

const dailyNeeded = (center) => (daysLeft > 0 ? Math.ceil((center.objective - center.revenue) / daysLeft) : null)

const gaugeOptions = (center) => ({
    chart: { type: 'radialBar', sparkline: { enabled: true } },
    colors: [COLORS[pace(center).key]],
    plotOptions: {
        radialBar: {
            startAngle: -120,
            endAngle: 120,
            hollow: { size: '66%' },
            track: { background: '#eff2f7', strokeWidth: '100%' },
            dataLabels: {
                name: { show: true, offsetY: 28, fontSize: '13px', color: '#878a99' },
                value: {
                    offsetY: -14,
                    fontSize: '30px',
                    fontWeight: 600,
                    color: '#495057',
                    // La jauge plafonne à 100 %, mais le libellé garde l'avancement réel.
                    formatter: () => formatPercent(center.progress),
                },
            },
        },
    },
    fill: { opacity: 1 },
    stroke: { lineCap: 'round' },
    labels: ["de l'objectif"],
})

// Plusieurs centres : totaux pour l'en-tête de la vue compacte
const isMultiCenter = computed(() => dashboard.value.centers.length > 1)
const totalRevenue = computed(() => dashboard.value.centers.reduce((sum, center) => sum + center.revenue, 0))
const totalObjective = computed(() => dashboard.value.centers.reduce((sum, center) => sum + (center.objective ?? 0), 0))
const centersWithObjective = computed(() => dashboard.value.centers.filter((center) => center.progress !== null).length)

const servicesTotal = computed(() => dashboard.value.services.reduce((sum, row) => sum + row.count, 0))

// Au-delà des plus fréquentes, les prestations sont regroupées : sinon la légende devient illisible.
const TOP_SERVICES = 6

const servicesChart = computed(() => {
    const services = dashboard.value.services
    const rows = services.slice(0, TOP_SERVICES)
    const othersCount = services.slice(TOP_SERVICES).reduce((sum, row) => sum + row.count, 0)
    if (othersCount > 0) {
        rows.push({ service: 'Autres', count: othersCount })
    }
    return {
        series: rows.map((row) => row.count),
        options: {
            chart: { type: 'donut' },
            labels: rows.map((row) => row.service),
            colors: rows.map((row, index) => serviceColor(row.service, index)),
            legend: { position: 'bottom', fontSize: '12px' },
            dataLabels: { enabled: false },
            stroke: { width: 2 },
            plotOptions: {
                pie: {
                    donut: {
                        size: '70%',
                        labels: {
                            show: true,
                            total: { show: true, label: 'Demandes', formatter: () => formatNumber(servicesTotal.value) },
                        },
                    },
                },
            },
        },
    }
})

onMounted(() => {
    axios.get('/api/centers/revenue-dashboard')
        .then((response) => {
            dashboard.value = response.data.data
        })
        .catch((error) => httpErrorsHandler(error))
        .finally(() => {
            loading.value = false
        })
})
</script>

<template>
    <div class="row" v-if="loading || dashboard.centers.length">
        <!-- Objectif de CA -->
        <div class="col-xl-7">
            <div class="card card-height-100">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1">
                        <i class="mdi mdi-bullseye-arrow me-2 align-middle fs-4"></i>
                        Objectif de chiffre d'affaires <span class="text-muted fw-normal" v-if="monthLabel">· {{ monthLabel }}</span>
                    </h4>
                    <i class="mdi mdi-information-outline text-muted fs-16" v-tooltip="REVENUE_HELP"></i>
                </div>
                <div class="card-body d-flex flex-column">
                    <div class="d-flex flex-grow-1 align-items-center justify-content-center py-5" v-if="loading">
                        <div class="spinner-border text-secondary" role="status"></div>
                    </div>
                    <!-- Plusieurs centres : une ligne compacte par centre -->
                    <template v-else-if="isMultiCenter">
                        <div class="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-2">
                            <div>
                                <p class="text-uppercase fw-medium text-muted fs-12 mb-1">CA réalisé · {{ dashboard.centers.length }} centres</p>
                                <h3 class="ff-secondary fw-semibold mb-0">{{ formatAmount(totalRevenue) }}</h3>
                            </div>
                            <div class="text-end" v-if="centersWithObjective">
                                <p class="text-uppercase fw-medium text-muted fs-12 mb-1">
                                    Objectifs cumulés<template v-if="centersWithObjective < dashboard.centers.length"> · {{ centersWithObjective }} centre{{ centersWithObjective > 1 ? 's' : '' }}</template>
                                </p>
                                <h5 class="mb-0">{{ formatAmount(totalObjective) }}</h5>
                            </div>
                        </div>
                        <div class="centers-list">
                            <div v-for="center in dashboard.centers" :key="center.center_id" class="border-top py-3">
                                <div class="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                                    <span class="fw-medium text-truncate">{{ center.center_name }}</span>
                                    <span class="text-nowrap">
                                        <span class="fw-semibold">{{ formatAmount(center.revenue) }}</span>
                                        <span class="text-muted fs-12" v-if="center.progress !== null"> / {{ formatAmount(center.objective) }}</span>
                                    </span>
                                </div>
                                <template v-if="center.progress !== null">
                                    <div class="position-relative my-2">
                                        <div class="progress progress-sm bg-light rounded-pill">
                                            <div
                                                class="progress-bar rounded-pill"
                                                :class="'bg-' + pace(center).key"
                                                role="progressbar"
                                                :style="{ width: Math.min(center.progress, 100) + '%' }"
                                            ></div>
                                        </div>
                                        <div class="pace-marker" :style="{ left: expectedProgress + '%' }" v-tooltip="`Attendu à date : ${formatPercent(expectedProgress)}`"></div>
                                    </div>
                                    <div class="d-flex justify-content-between fs-12">
                                        <span :class="'text-' + pace(center).key"><i class="align-middle me-1" :class="pace(center).icon"></i>{{ formatPercent(center.progress) }} · {{ pace(center).label }}</span>
                                        <span class="text-muted">Projection : {{ formatAmount(projection(center)) }}</span>
                                    </div>
                                </template>
                                <p class="fs-12 text-muted mb-0" v-else><i class="ri-flag-line align-middle me-1"></i>Aucun objectif fixé ce mois-ci</p>
                            </div>
                        </div>
                        <p class="text-muted fs-12 mb-0 mt-auto pt-2 border-top">
                            <i class="ri-calendar-line align-middle me-1"></i>{{ daysLeft }} jour{{ daysLeft > 1 ? 's' : '' }} restant{{ daysLeft > 1 ? 's' : '' }} dans le mois
                        </p>
                    </template>
                    <template v-else>
                        <div
                            v-for="(center, index) in dashboard.centers"
                            :key="center.center_id"
                            class="d-flex flex-column flex-grow-1"
                            :class="{ 'border-top pt-4 mt-4': index > 0 }"
                        >
                            <h5 class="fs-14 text-muted mb-3" v-if="dashboard.centers.length > 1">{{ center.center_name }}</h5>

                            <!-- Avec objectif -->
                            <template v-if="center.progress !== null">
                                <div class="row align-items-center g-4 flex-grow-1">
                                    <div class="col-md-5">
                                        <StatChart type="radialBar" :height="260" :series="[Math.min(center.progress, 100)]" :options="gaugeOptions(center)" />
                                    </div>
                                    <div class="col-md-7">
                                        <p class="text-uppercase fw-medium text-muted fs-12 mb-1">CA réalisé</p>
                                        <h1 class="ff-secondary fw-semibold mb-1">{{ formatAmount(center.revenue) }}</h1>
                                        <p class="text-muted mb-3">sur un objectif de <span class="fw-semibold text-body">{{ formatAmount(center.objective) }}</span></p>

                                        <div class="position-relative mb-2">
                                            <div class="progress progress-lg bg-light rounded-pill">
                                                <div
                                                    class="progress-bar rounded-pill"
                                                    :class="'bg-' + pace(center).key"
                                                    role="progressbar"
                                                    :style="{ width: Math.min(center.progress, 100) + '%' }"
                                                ></div>
                                            </div>
                                            <!-- Repère : avancement attendu à date -->
                                            <div class="pace-marker" :style="{ left: expectedProgress + '%' }" v-tooltip="`Attendu à date : ${formatPercent(expectedProgress)}`"></div>
                                        </div>
                                        <div class="d-flex justify-content-between fs-12 text-muted mb-3">
                                            <span>0 €</span>
                                            <span>{{ formatAmount(center.objective) }}</span>
                                        </div>

                                        <span class="badge fs-12 py-2 px-3" :class="`bg-${pace(center).key}-subtle text-${pace(center).key}`">
                                            <i class="align-middle me-1" :class="pace(center).icon"></i>{{ pace(center).label }}
                                        </span>
                                    </div>
                                </div>

                                <div class="row g-3 mt-2">
                                    <div class="col-6 col-lg-3">
                                        <div class="bg-light rounded p-3 h-100">
                                            <p class="text-muted fs-12 mb-1"><i class="ri-flag-line align-middle me-1"></i>Objectif</p>
                                            <h5 class="mb-0">{{ formatAmount(center.objective) }}</h5>
                                        </div>
                                    </div>
                                    <div class="col-6 col-lg-3">
                                        <div class="bg-light rounded p-3 h-100" v-if="center.revenue < center.objective">
                                            <p class="text-muted fs-12 mb-1"><i class="ri-hourglass-line align-middle me-1"></i>Reste à faire</p>
                                            <h5 class="mb-0">{{ formatAmount(center.objective - center.revenue) }}</h5>
                                        </div>
                                        <div class="bg-success-subtle rounded p-3 h-100" v-else>
                                            <p class="text-muted fs-12 mb-1"><i class="ri-medal-line align-middle me-1"></i>Dépassement</p>
                                            <h5 class="mb-0 text-success">+ {{ formatAmount(center.revenue - center.objective) }}</h5>
                                        </div>
                                    </div>
                                    <div class="col-6 col-lg-3">
                                        <div class="bg-light rounded p-3 h-100">
                                            <p class="text-muted fs-12 mb-1"><i class="ri-speed-line align-middle me-1"></i>Rythme nécessaire</p>
                                            <h5 class="mb-0" v-if="center.revenue < center.objective && dailyNeeded(center) !== null">{{ formatAmount(dailyNeeded(center)) }} <span class="fs-12 text-muted fw-normal">/ jour</span></h5>
                                            <h5 class="mb-0 text-muted" v-else>—</h5>
                                        </div>
                                    </div>
                                    <div class="col-6 col-lg-3">
                                        <div class="bg-light rounded p-3 h-100">
                                            <p class="text-muted fs-12 mb-1"><i class="ri-line-chart-line align-middle me-1"></i>Projection fin de mois</p>
                                            <h5 class="mb-0">{{ formatAmount(projection(center)) }}</h5>
                                        </div>
                                    </div>
                                </div>
                                <p class="text-muted fs-12 mb-0 mt-3">
                                    <i class="ri-calendar-line align-middle me-1"></i>{{ daysLeft }} jour{{ daysLeft > 1 ? 's' : '' }} restant{{ daysLeft > 1 ? 's' : '' }} dans le mois
                                </p>
                            </template>

                            <!-- Sans objectif -->
                            <div class="d-flex flex-column flex-grow-1 align-items-center justify-content-center text-center py-4" v-else>
                                <div class="avatar-lg mb-3">
                                    <span class="avatar-title bg-light text-muted rounded-circle fs-1">
                                        <i class="ri-flag-line"></i>
                                    </span>
                                </div>
                                <p class="text-uppercase fw-medium text-muted fs-12 mb-1">CA réalisé</p>
                                <h1 class="ff-secondary fw-semibold mb-2">{{ formatAmount(center.revenue) }}</h1>
                                <p class="text-muted mb-0">Aucun objectif n'est fixé pour ce mois.</p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Répartition des prestations -->
        <div class="col-xl-5">
            <div class="card card-height-100">
                <div class="card-header align-items-center d-flex">
                    <h4 class="card-title mb-0 flex-grow-1">
                        <i class="mdi mdi-chart-donut me-2 align-middle fs-4"></i>
                        Prestations demandées <span class="text-muted fw-normal" v-if="monthLabel">· {{ monthLabel }}</span>
                    </h4>
                    <i class="mdi mdi-information-outline text-muted fs-16" v-tooltip="SERVICES_HELP"></i>
                </div>
                <div class="card-body d-flex flex-column">
                    <div class="d-flex flex-grow-1 align-items-center justify-content-center py-5" v-if="loading">
                        <div class="spinner-border text-secondary" role="status"></div>
                    </div>
                    <!-- Le graphique occupe toute la hauteur laissée par la carte voisine (hauteur minimale une fois empilé sur mobile). -->
                    <div class="services-chart" v-else-if="dashboard.services.length">
                        <div class="services-chart-inner">
                            <StatChart type="donut" height="100%" :series="servicesChart.series" :options="servicesChart.options" />
                        </div>
                    </div>
                    <div class="d-flex flex-column flex-grow-1 align-items-center justify-content-center text-center text-muted py-5" v-else>
                        <i class="ri-pie-chart-line fs-1 d-block mb-2"></i>
                        Aucune demande avec prestation ce mois-ci
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.services-chart {
    position: relative;
    flex-grow: 1;
    min-height: 340px;
}

.services-chart-inner {
    position: absolute;
    inset: 0;
}

/* Au-delà de 6 centres environ, la liste défile plutôt que d'étirer la carte (et l'anneau voisin) */
.centers-list {
    max-height: 440px;
    overflow-x: hidden;
    overflow-y: auto;
    /* Le repère « attendu à date » dépasse de 1 px à 100 % : marge pour ne pas le couper */
    padding-right: 2px;
}

.pace-marker {
    position: absolute;
    top: -4px;
    bottom: -4px;
    width: 3px;
    margin-left: -1px;
    border-radius: 2px;
    background-color: var(--vz-body-color);
    opacity: 0.55;
    cursor: help;
}
</style>
